import cn from 'classnames';
import { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { envStrToBoolean, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useIsModuleLoaded } from 'src/hooks/use-is-module-loaded';
import useKycPopup from 'src/hooks/use-kycPopup';
import { useSessionRecorder } from 'src/hooks/use-session-recorder';
import { setSelectedCategory } from 'src/store/app/actions';
import { openDialog } from 'src/store/dialog/actions';
import { EnumDialogsKeys } from 'src/store/dialog/types';
import { setGameBoxMissions, setTournamentGame } from 'src/store/integrations/actions';
import { setBalance } from 'src/store/user/actions';
import { RootState } from 'src/types/store-types';
import './styles.scss';

// Define a functional component named 'Gos'
const CasinoTournament: FC = () => {
  const moduleName = 'casinoTournament';
  const isLoaded = useIsModuleLoaded(moduleName);

  const navigate = useNavigate();

  const [fullScreen, setFullScreen] = useState(false);
  const sessionRecorder = useSessionRecorder();

  const { selectedTournamentGame, showTournamentPage } = useSelector((state: RootState) => state.integrations);
  const { user, kycStatus } = useSelector((state: RootState) => state.user);
  const { section } = useSelector((state: RootState) => state.accountModal);

  const _requestGameBoxMission = useConnectedAction(setGameBoxMissions.req);
  const _resetExternalTournamentGame = useConnectedAction(setTournamentGame);
  const _requestBalance = useConnectedAction(setBalance.req);
  const _openDialog = useConnectedAction(openDialog);
  const _setSelectedCategory = useConnectedAction(setSelectedCategory);
  useKycPopup(kycStatus, user, section, _openDialog);
  const handleGameOpen = useCallback(() => {
    if (!isLoaded || !selectedTournamentGame) return;

    window.__gdlib?.notify('GAME_OPEN', selectedTournamentGame);
  }, [isLoaded, selectedTournamentGame]);

  const handleShowTournamentPage = useCallback(() => {
    if (!isLoaded) return;

    window.__gdlib?.notify('DIALOG_OPEN', 'howToPlay');
  }, [showTournamentPage]);

  useEffect(() => {
    if (!isLoaded) return;

    // Function to attempt starting the widget
    const startWidget = (): any => {
      if (!window.__gdlib) {
        return <div>...</div>;
      }

      window.__gdlib?.startGosWidget({
        parentId: 'casinoTournamentWidgetId', // ID for the widget's parent element
        customClassName: cn('frame', { ['fullScreen']: fullScreen }), // Custom class name for styling
        sdk: !envStrToBoolean(process.env.REACT_APP_GOS_FRAME),
      });
      return true; // Indicate that the widget has been started
    };

    // Attempt to start the widget immediately
    if (startWidget()) {
      return () => {
        // Close the widget when the component is unmounted
        window.__gdlib?.closeWidget(moduleName);
      };
    } // Exit if the widget was started

    // If the widget is not yet available, set up a MutationObserver to wait for changes in the DOM
    const observer = new MutationObserver(() => {
      // Check again if the widget can be started
      if (startWidget()) {
        observer.disconnect(); // Stop observing once the widget is successfully started
      }
    });

    // Start observing for changes in the DOM
    observer.observe(document.body, { childList: true, subtree: true });

    // Cleanup function to be called on component unmount
    return () => {
      // Close the widget when the component is unmounted
      window.__gdlib?.closeWidget(moduleName);
      observer.disconnect(); // Clean up the observer to prevent memory leaks
    };
  }, [isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;

    window.__gdlib?.registerCallBack('navigateToSelvaSeries', 'casinoTournament', () => {
      _setSelectedCategory('silvaSeries');
      navigate(`/category/silvaSeries`);
    });

    window.__gdlib?.registerCallBack('gameStateChanged', 'casinoTournament', (fullScreen: boolean) => {
      if (!fullScreen) {
        setTimeout(() => {
          if (user) {
            _requestGameBoxMission();
            _resetExternalTournamentGame(null);
          }
        }, 500);
      }
      setFullScreen(fullScreen);
    });

    window.__gdlib?.registerCallBack('spinAndGoStarted', 'casinoTournament', (tournamentId: string) => {
      _openDialog({
        dialogType: EnumDialogsKeys.SPIN_AND_GO_STARTED,
        dialogProps: { data: { tournamentId: tournamentId } },
      });
    });

    window.__gdlib?.registerCallBack('scrollLock', 'casinoTournament', (value: 'set' | 'unset') => {
      setBodyOverflow(value ? 'set' : 'unset');
    });

    window.__gdlib?.registerCallBack('resumeTournament', 'casinoTournament', (data: any) => {
      _openDialog({
        dialogType: EnumDialogsKeys.RESUME_TOURNAMENT,
        dialogProps: { data },
      });
    });

    window.__gdlib?.registerCallBack('successfullRegistration', 'casinoTournament', (id: string) => {
      localStorage.setItem('resumeGameId', id);
    });

    window.__gdlib?.registerCallBack('successfullUnregistration', 'casinoTournament', () => {
      localStorage.removeItem('resumeGameId');
    });

    window.__gdlib?.registerCallBack('balanceUpdate', 'casinoTournament', () => {
      _requestBalance();
    });

    return () => {
      window.__gdlib?.unRegisterCallBack('navigateToSelvaSeries', 'casinoTournament');
      window.__gdlib?.unRegisterCallBack('gameStateChanged', 'casinoTournament');
      window.__gdlib?.unRegisterCallBack('spinAndGoStarted', 'casinoTournament');
      window.__gdlib?.unRegisterCallBack('scrollLock', 'casinoTournament');
      window.__gdlib?.unRegisterCallBack('resumeTournament', 'casinoTournament');
      window.__gdlib?.unRegisterCallBack('successfullRegistration', 'casinoTournament');
      window.__gdlib?.unRegisterCallBack('successfullUnregistration', 'casinoTournament');
      window.__gdlib?.unRegisterCallBack('balanceUpdate', 'casinoTournament');
    };
  }, [isLoaded]);

  useEffect(() => {
    sessionRecorder?.trackEvent('Gos page view');
  }, []);

  useEffect(() => {
    handleGameOpen();
  }, [handleGameOpen]);

  useEffect(() => {
    handleShowTournamentPage();
  }, [handleShowTournamentPage]);

  // Render a div element with the specified ID for the widget
  return <div id="casinoTournamentWidgetId" className="casinoTournament fullWidth page_content" />;
};

export default CasinoTournament; // Export the Gos component for use in other parts of the application
